import { get } from 'lodash';
import React from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import { AppointmentRequirements } from '../AppointmentRequirements';
import { FormRow } from '@zocdoc-frontend-common/styles-grid';
import Question from '@zocdoc-frontend-common/assets-icons/Question';
import { Checkbox } from '@zocdoc-frontend-common/components-checkbox';
import { slate70 } from '@zocdoc-mezzanine/foundations-colors';
import { Tooltip, TOOLTIP } from '@zocdoc-frontend-common/components-tooltip';
import { FontFamily } from '@zocdoc-frontend-common/styles-typography';
import Textbox from '@zocdoc-frontend-common/components-textbox';
import { APPOINTMENT_DETAILS_STRINGS } from './constants';
import { MEMBER_ID_SOURCE_TYPE } from '../../constants';

const renderOptionalText = () => {
    return <OptionalText>(optional)</OptionalText>;
};

const getToolTipText = (insuranceIdSourceType, isRequired) => {
    if (insuranceIdSourceType === MEMBER_ID_SOURCE_TYPE.INSURANCE_API) {
        if (isRequired) {
            return APPOINTMENT_DETAILS_STRINGS.MemberIdFromCardImageTooltipContent;
        }
        return APPOINTMENT_DETAILS_STRINGS.MemberIdFromCardImageTooltipContentWithOptionalText;
    }
    return APPOINTMENT_DETAILS_STRINGS.TooltipContent;
};

const renderPatientInsuranceMemberIdInput = props => {
    const {
        insuranceError,
        insuranceId,
        insuranceIdSourceType,
        insuranceIdRequired,
        onEnterInsuranceId,
        shouldDisableMemberIdField,
        trackSelectInsuranceId,
        trimInsuranceId,
    } = props;

    const insuranceErrorMessage = insuranceError
        ? APPOINTMENT_DETAILS_STRINGS.InsuranceIdRequired
        : undefined;

    const tooltipContent = getToolTipText(
        insuranceIdSourceType,
        insuranceIdRequired
    );
    const tooltipAnchor = <Question color={slate70} height={16} />;

    return (
        <FormRow>
            <div data-test="patient-insurance-header">
                <FormHeader>Insurance Member ID</FormHeader>{' '}
                {!insuranceIdRequired && renderOptionalText()}
                <QuestionIconWrapper id="question-icon-wrapper">
                    <StyledTooltip
                        anchor={tooltipAnchor}
                        showTooltipOnFocus
                        showTooltipOnHover
                        tooltipPosition={TOOLTIP.BOTTOM}
                    >
                        {tooltipContent}
                    </StyledTooltip>
                </QuestionIconWrapper>
                <TextboxWrapper data-test="patient-insurance-member-id-textbox-div">
                    <Textbox
                        className="exclude-from-playback"
                        data-test="patient-insurance-member-id-textbox"
                        data-uem-id="patient-insurance-member-id-textbox"
                        disabled={shouldDisableMemberIdField}
                        fluid
                        invalidMessage={insuranceErrorMessage}
                        onBlur={trimInsuranceId}
                        onChange={onEnterInsuranceId}
                        onFocus={trackSelectInsuranceId}
                        placeholder={
                            shouldDisableMemberIdField && !insuranceId
                                ? 'N/A'
                                : ''
                        }
                        value={insuranceId}
                    />
                </TextboxWrapper>
            </div>
        </FormRow>
    );
};

const renderFakeAppointmentInput = props => {
    const { bookingState, onFakeAppointmentChanged } = props;

    if (!bookingState || bookingState.is_fake_appointment === undefined) {
        return undefined;
    }

    return (
        <FormRow>
            <div data-test="is-fake-appointment-header">
                <FormHeader>This appointment is fake:</FormHeader>
                <Checkbox
                    checked={bookingState.is_fake_appointment}
                    data-test="fake-appointment-input"
                    height={16}
                    onChecked={e => {
                        onFakeAppointmentChanged(e, bookingState);
                    }}
                />
            </div>
        </FormRow>
    );
};

const AppointmentDetailsFormView = props => {
    const {
        bookingState,
        insuranceName,
        professionalName,
        shouldRenderAppointmentRequirements,
        shouldShowInsuranceMemberIdInput,
    } = props;

    if (
        !bookingState ||
        (!bookingState.is_fake_appointment &&
            !shouldShowInsuranceMemberIdInput &&
            !shouldRenderAppointmentRequirements)
    ) {
        return null;
    }

    return (
        <React.Fragment>
            {shouldShowInsuranceMemberIdInput &&
                renderPatientInsuranceMemberIdInput(props)}
            {renderFakeAppointmentInput(props)}
            {shouldRenderAppointmentRequirements && (
                <AppointmentRequirements
                    insuranceName={insuranceName}
                    patientId={get(bookingState, 'patient_id', 0)}
                    professionalName={professionalName}
                    requirements={get(bookingState, 'requirements', [])}
                />
            )}
        </React.Fragment>
    );
};

AppointmentDetailsFormView.propTypes = {
    shouldRenderAppointmentRequirements: PropTypes.bool,
    shouldShowInsuranceMemberIdInput: PropTypes.bool.isRequired,
    shouldDisableMemberIdField: PropTypes.bool,
    bookingState: PropTypes.object.isRequired,
    professionalName: PropTypes.string.isRequired,
    insuranceName: PropTypes.string.isRequired,
    trimInsuranceId: PropTypes.func.isRequired,
    insuranceId: PropTypes.string.isRequired,
    insuranceIdRequired: PropTypes.bool.isRequired,
    insuranceError: PropTypes.bool.isRequired,
    onEnterInsuranceId: PropTypes.func.isRequired,
    onFakeAppointmentChanged: PropTypes.func.isRequired,
    onEnterDoctorNotes: PropTypes.func.isRequired,
    trackSelectInsuranceId: PropTypes.func.isRequired,
    trackSelectDoctorNotes: PropTypes.func.isRequired,
};

AppointmentDetailsFormView.defaultProps = {
    shouldRenderAppointmentRequirements: true,
};

export const StyledTooltip = styled(Tooltip)`
    margin-left: 50%;
    transform: translateX(-50%);
    width: 250px;
`;

const QuestionIconWrapper = styled.div`
    display: inline-block;
    height: 16px;
    margin-left: 8px;
    vertical-align: middle;
    width: 16px;
`;

const FormHeader = styled.span`
    font: 15px/24px ${FontFamily.SharpSansSemibold};
`;

const OptionalText = styled.span`
    font: 15px/24px ${FontFamily.SharpSansMedium};
    padding-top: 1px;
    padding-bottom: 3px;
`;

const TextboxWrapper = styled.div`
    margin-top: 4px;
    position: relative;

    input {
        padding-right: 37px;
    }
`;

export default AppointmentDetailsFormView;
